import React from 'react'
import { useTranslation } from 'react-i18next'
import { ProfileData, CSProfileData } from '@/types'
import cashIcon from '../../assets/images/cash.svg'
import onlineIcon from '../../assets/images/online.svg'
import partialIcon from '../../assets/images/partial.svg'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import SectionTitle from './SectionTitle'

interface SalesClassificationTableProps {
  profile: ProfileData | CSProfileData
  isCS: boolean
  formatNumber: (num: number | string | undefined | null) => string
}

// LTR Number component (matching Flutter's Directionality)
const LTRNum: React.FC<{
  text: string
  style?: React.CSSProperties
}> = ({ text, style }) => {
  return (
    <span style={{ direction: 'ltr', display: 'inline-block', ...style }}>
      {text}
    </span>
  )
}

const SalesClassificationTable: React.FC<SalesClassificationTableProps> = ({
  profile,
  isCS,
  formatNumber,
}) => {
  const { t } = useTranslation()

  return (
    <div className="bg-white rounded-2xl p-4 flex flex-col gap-2.5">
      <SectionTitle titleKey="sales_classification" />
      <div className="h-2.5" /> {/* SizedBox(height: 10) */}

      {/* Scrollable Content Container */}
      <div className="overflow-y-auto max-h-[400px] pr-1" style={{ scrollbarWidth: 'thin' }}>
        {/* Header Bar */}
        <div
          className="h-9 px-2.5 rounded-[10px] flex items-center sticky top-0 z-10"
          style={{ backgroundColor: '#F5F5F5' }}
        >
          <div className="flex-1 text-start text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {t('operation_type')}
          </div>
          <div className="flex-1 text-center text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {t('operation_count')}
          </div>
          <div className="flex-1 text-end text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
            {t('total_orders')}
          </div>
        </div>

        <div className="h-1.5" /> {/* SizedBox(height: 6) */}

        {/* Cash Row */}
        <div className="px-1.5 py-1.5">
          <div className="flex items-center">
            <div className="flex-1 flex items-center gap-1.5">
              <img src={cashIcon} alt="cash" className="w-3.5 h-3.5" />
              <span className="text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
                {t('cash')}
              </span>
            </div>
            <div className="flex-1 flex justify-center">
              <LTRNum
                text={formatNumber(isCS ? profile.cashOrdersCount : (profile as ProfileData).cashOrdersCount)}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
            </div>
            <div className="flex-1 flex justify-end items-center gap-1">
              <LTRNum
                text={formatNumber(isCS ? profile.cashSales : (profile as ProfileData).cashSales)}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
              <CurrencyDisplay className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        <div className="h-1.5" /> {/* SizedBox(height: 6) */}

        {/* Network/Online Row (with grey background) */}
        <div
          className="px-1.5 py-1.5 rounded-[10px]"
          style={{ backgroundColor: '#F5F5F5' }}
        >
          <div className="flex items-center">
            <div className="flex-1 flex items-center gap-1.5">
              <img src={onlineIcon} alt="network" className="w-3.5 h-3.5" />
              <span className="text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
                {isCS ? t('online') : t('network')}
              </span>
            </div>
            <div className="flex-1 flex justify-center">
              <LTRNum
                text={formatNumber(
                  isCS
                    ? (profile as CSProfileData).onlineOrdersCount
                    : (profile as ProfileData).networkOrdersCount
                )}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
            </div>
            <div className="flex-1 flex justify-end items-center gap-1">
              <LTRNum
                text={formatNumber(
                  isCS
                    ? (profile as CSProfileData).onlineSales
                    : (profile as ProfileData).networkSales
                )}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
              <CurrencyDisplay className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        <div className="h-1.5" /> {/* SizedBox(height: 6) */}

        {/* Partial/Wallet Row */}
        <div className="px-1.5 py-1.5">
          <div className="flex items-center">
            <div className="flex-1 flex items-center gap-1.5">
              <img src={partialIcon} alt="partial" className="w-3 h-3" />
              <span className="text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
                {isCS ? t('wallet') : t('partial_payment')}
              </span>
            </div>
            <div className="flex-1 flex justify-center">
              <LTRNum
                text={formatNumber(
                  isCS
                    ? (profile as CSProfileData).walletOrdersCount
                    : (profile as ProfileData).partialOrdersCount
                )}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
            </div>
            <div className="flex-1 flex justify-end items-center gap-1">
              <LTRNum
                text={formatNumber(
                  isCS
                    ? (profile as CSProfileData).walletSales
                    : (profile as ProfileData).partialSales
                )}
                style={{
                  fontFamily: 'IBM_Plex_Sans_Arabic',
                  fontSize: '12px',
                  color: '#000000',
                }}
              />
              <CurrencyDisplay className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesClassificationTable
